import { useEffect, useState } from 'react';
import { Navigate, Outlet, matchPath, useLocation, useParams } from 'react-router-dom';
import Layout from '~/components/shared/layout/Layout';
import MainTitle from '~/components/shared/layout/MainTitle';
import Loader from '~/components/shared/loader/Loader';
import { SupportFooter } from '~/components/shared/support-footer/SupportFooter';
import { ExpandableBasket } from './components/expandable-basket/ExpandableBasketTestVersion';
import { StepListContent } from './components/steps/StepList';
import { useBasket } from './useBasket';

import { useTitleCallback } from '~/hooks/useTitle';
import { useInternetFlowStore } from '../internet/useInternetStore';
import { campaignRoutes, useFlowSteps } from './campaign.routes';
import { useMappedCampaign } from './campaign.utils';
import { StepNavigation } from './components/steps/StepNavigation';
import { FunnelAnalytics } from './FunnelAnalytics';

export const Campaign = () => {
  const setTitle = useTitleCallback();
  const { pathname } = useLocation();
  // we know that "campaignUrl" is defined here
  const { campaignUrl = '' } = useParams<{ campaignUrl: string }>();

  const dwellingUnit = useBasket((s) => s.dwellingUnit);
  const partnerAlias = useBasket((s) => s.partnerAlias);
  const selectedProducts = useBasket((s) => s.selectedProducts);
  const isSummaryOpen = useBasket((s) => s.isSummaryOpen);
  const toggleSummary = useBasket((s) => s.toggleSummary);
  const resetBasketForCampaign = useBasket((s) => s.resetBasketForCampaign);
  // basket state is reset on campaign change
  const { data, error } = useMappedCampaign(partnerAlias, dwellingUnit, campaignUrl);
  const [isBasketReset, setIsBasketReset] = useState(false);

  useEffect(() => {
    if (data) {
      // Add internet product if it exists
      resetBasketForCampaign(data, useInternetFlowStore.getState().selectedBroadBandProduct);

      // must wait for reset to render since we otherwise clear selected bundleId etc after render
      setIsBasketReset(true);
    }
  }, [data, resetBasketForCampaign]);

  const flowsteps = useFlowSteps();

  if (error || !campaignUrl) {
    return <Navigate to={campaignRoutes.index} />;
  }
  if (!data || !isBasketReset) {
    return <Loader />;
  }

  // Figure out current step
  const stepRoute = flowsteps.find((route) => matchPath(`${campaignRoutes.absoluteStepPathBase}/${route.stepPath}`, pathname));
  if (!stepRoute) {
    console.error('Step route not found', pathname, flowsteps);
    return <Navigate to={campaignRoutes.index} />;
  }
  const currentStep = flowsteps.indexOf(stepRoute);
  const currentStepPlusOne = currentStep + 1;
  const mainSubscriptionName = data.products.subscription[0].offers[0].displayData.name;
  setTitle(`${mainSubscriptionName} - Steg ${currentStepPlusOne}: ${stepRoute.title} `, true);

  return (
    <div>
      <FunnelAnalytics campaign={data} currentStep={currentStep} />
      {/* STEPS */}
      <div className="campaign light">
        <MainTitle>Bestilling av {mainSubscriptionName}</MainTitle>
        <div className="steps" aria-hidden={isSummaryOpen}>
          <nav className="steps-header" aria-label="Stegindikator bestillingsløp" tabIndex={-1}>
            <StepListContent steps={flowsteps} currentStep={currentStep} />
          </nav>
          <Layout
            focusOnRender={true}
            wide={stepRoute.wideLayout}
            aria-label={`Bestilling av ${mainSubscriptionName}, Steg ${currentStepPlusOne} av ${flowsteps.length} - ${stepRoute.title}`}
          >
            {/* render current step / child route */}
            <Outlet context={data} />
          </Layout>
        </div>
        {stepRoute?.stepPath !== campaignRoutes.campaignSummary && (
          <ExpandableBasket selectedProducts={selectedProducts} hideShipping={false} isOpen={isSummaryOpen} toggleSummary={toggleSummary} />
        )}
        <StepNavigation isSummaryOpen={isSummaryOpen} currentStep={currentStep} />
      </div>
      <SupportFooter accommodateShoppingCart />
    </div>
  );
};
